import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LiveChat } from './components/LiveChat';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { ScrollToTopButton } from './components/ScrollToTopButton';
import { PageTransition } from './components/PageTransition';
// Home is the landing route — keep it eager so the first paint is instant.
import { Home } from './pages/Home';
import { CartProvider } from './components/CartContext';
import { CartDrawer } from './components/CartDrawer';
import { AnnouncementBar } from './components/AnnouncementBar';
import { AddToHomeScreen } from './components/AddToHomeScreen';
import { I18nProvider } from './utils/i18n';
import { trackMetaPageView } from './utils/metaPixel';
// Every other route is code-split and fetched on demand, so they no longer
// weigh down the initial bundle. Named exports are mapped to a default for lazy.
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Product = lazy(() => import('./pages/Product').then(m => ({
  default: m.Product
})));
const Project = lazy(() => import('./pages/Project').then(m => ({
  default: m.Project
})));
const Projects = lazy(() => import('./pages/Projects').then(m => ({
  default: m.Projects
})));
const HelpMeChoose = lazy(() => import('./pages/HelpMeChoose').then(m => ({
  default: m.HelpMeChoose
})));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({
  default: m.AboutPage
})));
const DeliveryZones = lazy(() => import('./pages/DeliveryZones').then(m => ({
  default: m.DeliveryZones
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const DeliveryPrep = lazy(() => import('./pages/DeliveryPrep').then(m => ({
  default: m.DeliveryPrep
})));
const CompareMaterials = lazy(() => import('./pages/CompareMaterials').then(m => ({
  default: m.CompareMaterials
})));
const FallPrep = lazy(() => import('./pages/FallPrep').then(m => ({
  default: m.FallPrep
})));
const TonsToYards = lazy(() => import('./pages/TonsToYards').then(m => ({
  default: m.TonsToYards
})));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({
  default: m.NotFound
})));
const TrackOrder = lazy(() => import('./pages/TrackOrder').then(m => ({
  default: m.TrackOrder
})));
const TrackOrderLookup = lazy(() => import('./pages/TrackOrderLookup').then(m => ({
  default: m.TrackOrderLookup
})));
const Calculator = lazy(() => import('./pages/Calculator').then(m => ({
  default: m.Calculator
})));
const CityMaterial = lazy(() => import('./pages/CityMaterial').then(m => ({
  default: m.CityMaterial
})));
const LocationLanding = lazy(() => import('./pages/LocationLanding').then(m => ({
  default: m.LocationLanding
})));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas').then(m => ({
  default: m.ServiceAreas
})));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy').then(m => ({
  default: m.RefundPolicy
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({
  default: m.TermsOfService
})));
const DailyTicketForm = lazy(() => import('./pages/DailyTicketForm').then(m => ({
  default: m.DailyTicketForm
})));
function RouteFallback() {
  return <div className="min-h-[60vh] flex items-center justify-center bg-[#fdfbf7] dark:bg-[#222222]" role="status" aria-label="Loading" data-id="element-0" data-mp="1m56364zb7klx">
      <span className="h-8 w-8 rounded-full border-2 border-brand-orange border-t-transparent animate-spin" data-id="element-1" data-mp="6o5v9le4moj9" />
    </div>;
}
// Client-side routing never reloads the document, so the pixel's initial
// PageView is the only one it would ever see. This reports each subsequent
// route as its own page view.
function MetaPixelPageViews() {
  const location = useLocation();
  useEffect(() => {
    trackMetaPageView();
  }, [location.pathname]);
  return null;
}
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Two-pass scroll: first attempt accounts for the sticky navbar height,
      // then a follow-up corrects for late-rendering content (Services, etc.)
      // that can push the target further down the page.
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (!el) return;
        const navHeight = document.querySelector('nav')?.getBoundingClientRect().height || 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      };
      const t1 = setTimeout(scrollToTarget, 120);
      const t2 = setTimeout(scrollToTarget, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}
function AnimatedRoutes() {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-2" data-mp="29xo5a333ouvk">
      <Suspense fallback={<RouteFallback data-id="element-4" data-mp="2eu4xb8df7tdx" />} data-id="element-3" data-mp="2evpp4q8coy83">
        <Routes location={location} key={location.pathname} data-id="element-5" data-mp="2ex0uuvlcwg1u">
          <Route path="/" element={<PageTransition data-id="element-7" data-mp="2js6e1mndjgm8">
                <Home data-id="element-8" data-mp="2nvl6xdwevrh3" />
              </PageTransition>} data-id="element-6" data-mp="2jsybz44g1qfo" />
          <Route path="/contact" element={<PageTransition data-id="element-10" data-mp="2ntrypu65gz6z">
                <Contact data-id="element-11" data-mp="2sv7kxpk2dbqy" />
              </PageTransition>} data-id="element-9" data-mp="2nww7l4uk8j3z" />
          <Route path="/shop/:slug" element={<PageTransition data-id="element-13" data-mp="thtphs7uc3b3">
                <Product data-id="element-14" data-mp="timwmuapoutr" />
              </PageTransition>} data-id="element-12" data-mp="2sslddpx6aw8q" />
          <Route path="/projects" element={<PageTransition data-id="element-16" data-mp="yhpthrgjfuqn">
                <Projects data-id="element-17" data-mp="ygwwhi7dsjlp" />
              </PageTransition>} data-id="element-15" data-mp="ygojlqb9deai" />
          <Route path="/projects/:slug" element={<PageTransition data-id="element-19" data-mp="21ru85ih3wnhz">
                <Project data-id="element-20" data-mp="1wshb64wiu01o" />
              </PageTransition>} data-id="element-18" data-mp="21qsk8dezs6iu" />
          <Route path="/help" element={<PageTransition data-id="element-22" data-mp="1wuu0x48itepa">
                <HelpMeChoose data-id="element-23" data-mp="2ass37b9khkof" />
              </PageTransition>} data-id="element-21" data-mp="1wu23roz9z8ww" />
          <Route path="/about" element={<PageTransition data-id="element-25" data-mp="25tfesm31s5jq">
                <AboutPage data-id="element-26" data-mp="25se44ipy1l2j" />
              </PageTransition>} data-id="element-24" data-mp="2aq5v9airl35o" />
          <Route path="/delivery-zones" element={<PageTransition data-id="element-28" data-mp="2kpeo201w8bqg">
                <DeliveryZones data-id="element-29" data-mp="2kobwdpwqy59h" />
              </PageTransition>} data-id="element-27" data-mp="2kq6i36fue1em" />
          {/* 3/4" Crushed Clear was consolidated into 5/8" Crushed Clear —
              the same washed, no-fines rock within a gradation tolerance.
              Keeps the old product URL (and any link to it) alive. */}
          <Route path="/shop/3-4-clean-rock" element={<Navigate to="/shop/5-8-clear" replace data-id="element-31" data-mp="33go7p25dogvr" />} data-id="element-30" data-mp="2phogpnue7pwf" />
          {/* Builders Sand was renamed Utility Sand. */}
          <Route path="/shop/builders-sand" element={<Navigate to="/shop/utility-sand" replace data-id="element-33" data-mp="2yjo1mr8j3e4n" />} data-id="element-32" data-mp="2yicwamzfpybf" />
          <Route path="/subcontractors" element={<Navigate to="/" replace data-id="element-35" data-mp="3i466xl068jas" />} data-id="element-34" data-mp="3i33bczrwlsoj" />
          <Route path="/contractor-ordering" element={<Navigate to="/" replace data-id="element-37" data-mp="3n1ephmp8epck" />} data-id="element-36" data-mp="3n2fzroyfb7t8" />
          <Route path="/diy-help" element={<Navigate to="/blog/perfect-gravel-driveway" replace data-id="element-39" data-mp="385ysp9x9ofch" />} data-id="element-38" data-mp="3n0dbb9cx5mqm" />
          <Route path="/blog" element={<PageTransition data-id="element-41" data-mp="3d40gu99jv4ut">
                <Blog data-id="element-42" data-mp="3d2yv9auwqzyu" />
              </PageTransition>} data-id="element-40" data-mp="387teeqgd4bhm" />
          <Route path="/blog/:slug" element={<PageTransition data-id="element-44" data-mp="2zcda1fhzedxs">
                <BlogPost data-id="element-45" data-mp="2zb28lmc0di9u" />
              </PageTransition>} data-id="element-43" data-mp="2zdexykk3iuwx" />
          <Route path="/delivery-prep" element={<PageTransition data-id="element-47" data-mp="345ybjhis9q1q">
                <DeliveryPrep data-id="element-48" data-mp="2pf7lof0r5119" />
              </PageTransition>} data-id="element-46" data-mp="344wyx8m4cvhw" />
          <Route path="/compare" element={<PageTransition data-id="element-50" data-mp="2ue23bdmzgjjs">
                <CompareMaterials data-id="element-51" data-mp="2uf3poe9g8sgt" />
              </PageTransition>} data-id="element-49" data-mp="2pgsdvxzlg7vy" />
          <Route path="/tons-to-yards" element={<PageTransition data-id="element-53" data-mp="2gfc4ntkimbtb">
                <TonsToYards data-id="element-54" data-mp="2ggdrswet2ore" />
              </PageTransition>} data-id="element-52" data-mp="2ge9h9vmeiri5" />
          {/* Seasonal campaign landing page. Stays routable year-round — the
              page itself softens its urgency copy out of season — so inbound
              links and ads never break between seasons. */}
          <Route path="/fall-prep" element={<PageTransition data-id="element-56" data-mp="2vug3u2hzkf5q">
                <FallPrep data-id="element-57" data-mp="2qwoad41p0kzf" />
              </PageTransition>} data-id="element-55" data-mp="2vxlpt8vbj0x8" />
          <Route path="/calculator" element={<PageTransition data-id="element-59" data-mp="2lymhxsidn9ba">
                <Calculator data-id="element-60" data-mp="2lxbddqh3xxj4" />
              </PageTransition>} data-id="element-58" data-mp="2qzahj2ko8yh4" />
          {/* "Why shop local" is now the second half of the About page, so the
              old standalone URL redirects there rather than 404ing. */}
          <Route path="/local-vs-national" element={<Navigate to="/about" replace data-id="element-62" data-mp="286pw267axvnc" />} data-id="element-61" data-mp="2d487q4cpeoz1" />
          <Route path="/track" element={<PageTransition data-id="element-64" data-mp="284lu3tsyr6pp">
                <TrackOrderLookup data-id="element-65" data-mp="237v9ct67rmxy" />
              </PageTransition>} data-id="element-63" data-mp="287hqvet2rpck" />
          <Route path="/track/:orderId" element={<PageTransition data-id="element-67" data-mp="1y91vv0qxfvxs">
                <TrackOrder data-id="element-68" data-mp="1y78sbubm1ru0" />
              </PageTransition>} data-id="element-66" data-mp="235ini4xc4sfm" />
          <Route path="/service-areas" element={<PageTransition data-id="element-70" data-mp="1u888q9lb6l5y">
                <ServiceAreas data-id="element-71" data-mp="1u9azmhimsnlv" />
              </PageTransition>} data-id="element-69" data-mp="1u76ujw8zxik0" />
          <Route path="/refund-policy" element={<PageTransition data-id="element-73" data-mp="9r8uik4zw8wy">
                <RefundPolicy data-id="element-74" data-mp="dpyfoyw9yur5" />
              </PageTransition>} data-id="element-72" data-mp="9o4hqydgs4uo" />
          <Route path="/privacy-policy" element={<PageTransition data-id="element-76" data-mp="ip2rlxal36l6">
                <PrivacyPolicy data-id="element-77" data-mp="iqvud1i2t6nw" />
              </PageTransition>} data-id="element-75" data-mp="dskmuxf9788u" />
          <Route path="/terms-of-service" element={<PageTransition data-id="element-79" data-mp="no5kykh7bodb">
                <TermsOfService data-id="element-80" data-mp="nmb8zvnwr6ld" />
              </PageTransition>} data-id="element-78" data-mp="np783nbhs1be" />
          <Route path="/form" element={<PageTransition data-id="element-82" data-mp="3m4ghwjzjbg2u">
                <DailyTicketForm data-id="element-83" data-mp="3r4utud49ba65" />
              </PageTransition>} data-id="element-81" data-mp="3m7vyxscrqz6w" />

          {/* County- and city-level landing pages share this one-segment
              path; LocationLanding picks the right page for the slug and
              renders the 404 page for anything else. Distinct from the
              two-segment city × material route below — React Router matches
              on segment count, so the two coexist. */}
          <Route path="/gravel-delivery/:locationSlug" element={<PageTransition data-id="element-85" data-mp="38cjwszwaa6fy">
                <LocationLanding data-id="element-86" data-mp="2f7wsdafry8n0" />
              </PageTransition>} data-id="element-84" data-mp="38duymu66544f" />
          <Route path="/gravel-delivery/:citySlug/:materialSlug" element={<PageTransition data-id="element-88" data-mp="2k4n79ufuey6u">
                <CityMaterial data-id="element-89" data-mp="2k81crbjsznii" />
              </PageTransition>} data-id="element-87" data-mp="2f9hkyuij3hi8" />
          <Route path="*" element={<PageTransition data-id="element-91" data-mp="2p3889w2x5lwc">
                <NotFound data-id="element-92" data-mp="2u44am57mgzzn" />
              </PageTransition>} data-id="element-90" data-mp="2p6mbf6jepz4u" />
        </Routes>
      </Suspense>
    </AnimatePresence>;
}
function AppContent({
  isDark,
  toggleDark
}: {
  isDark: boolean;
  toggleDark: () => void;
}) {
  const location = useLocation();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  // The daily ticket form (/form) is a standalone operational document. It
  // renders bare — no site chrome (navbar, footer, announcement bar, live
  // chat, sticky CTA, cart) — so it prints clean and stays out of the way.
  const isStandalonePage = location.pathname === '/form';
  if (isStandalonePage) {
    // The daily ticket is a printed white document — never apply the `dark`
    // class here so it always renders on a clean white surface regardless of
    // the visitor's theme.
    return <div className="min-h-screen font-sans" data-id="element-93" data-mp="3rvsuld6snk3w">
        <ScrollToHash data-id="element-94" data-mp="3my150ptmga2v" />
        <MetaPixelPageViews data-id="element-95" data-mp="3mxix2vxlk7f4" />
        <AnimatedRoutes data-id="element-96" data-mp="3n0x1satqgspq" />
      </div>;
  }
  return <div className={`min-h-screen flex flex-col font-sans ${isDark ? 'dark' : ''} ${isContractorPage ? 'contractor-theme' : ''}`} data-id="element-97" data-mp="3i0itcro7zgr6">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-brand-orange focus:text-white focus:rounded-lg focus:font-bold focus:outline-none focus:ring-2 focus:ring-white" data-id="element-98" data-mp="3d27icdm6v07d">
        Skip to main content
      </a>
      <div className="flex-grow bg-[#fdfbf7] dark:bg-[#222222] transition-colors duration-300 pb-16 md:pb-0" id="main-content" data-id="element-99" data-mp="2zb2ro10sti96">
        <ScrollToHash data-id="element-100" data-mp="2pb23o1rzidyn" />
        <MetaPixelPageViews data-id="element-101" data-mp="2pak1ypmjz7jc" />
        <AnnouncementBar data-id="element-102" data-mp="2pcda69ctdztg" />
        <AddToHomeScreen data-id="element-103" data-mp="2pd529a77dfez" />
        <Navbar isDark={isDark} toggleDark={toggleDark} data-id="element-104" data-mp="2fnpq8igs53ts" />
        <AnimatedRoutes data-id="element-105" data-mp="2fm4yt1pri105" />
        <Footer data-id="element-106" data-mp="2kmj6ujrd5ay6" />
        <LiveChat data-id="element-107" data-mp="2kn2dv5fh6exf" />
        <StickyMobileCTA data-id="element-108" data-mp="2knkioqg7e1gy" />
        <ScrollToTopButton data-id="element-109" data-mp="2ko3yo1mamfsc" />
        <CartDrawer data-id="element-110" data-mp="2kk6j1pyxc6d7" />
      </div>
    </div>;
}
// Decide the initial theme for a first-time visitor (no saved preference yet):
//   1. An explicit saved choice always wins — a returning visitor who picked a
//      theme keeps it.
//   2. Everyone else starts in LIGHT mode.
//
// Light is the deliberate default. Customers are contractors and homeowners
// pricing material outdoors on a phone in daylight, so light mode is the
// higher-value surface and the one held to the tightest design standard. The
// previous behavior deferred to the OS `prefers-color-scheme` and then to a
// time-of-day clock rule, which meant a majority of first-time visitors landed
// in dark mode by accident — and made the very first impression the mode that
// gets less design attention. The toggle still switches modes and still
// persists, so nothing is taken away from anyone who prefers dark.
function getInitialTheme(): boolean {
  try {
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
  } catch {
    // localStorage can throw in private mode / embedded webviews / when
    // storage is blocked. Fall through to the light default.
  }
  return false;
}
export function App() {
  const [isDark, setIsDark] = useState(getInitialTheme);
  useEffect(() => {
    try {
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    } catch {}
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);
  const toggleDark = () => setIsDark(!isDark);
  return <I18nProvider data-id="element-111" data-mp="2d3054wacat09">
      <Router data-id="element-112" data-mp="2d2iag43bsmug">
        <CartProvider data-id="element-113" data-mp="2d1yvmw8z2ekn">
          <AppContent isDark={isDark} toggleDark={toggleDark} data-id="element-114" data-mp="2d1grldg2iw26" />
        </CartProvider>
      </Router>
    </I18nProvider>;
}